import React, { useRef } from "react"
import { Helmet } from "react-helmet"
import {
  CONSENT_VERSION,
  CookieConsentRecord,
  COOKIE_CONSENT_INITIAL_PREFERENCES,
  COOKIE_CONSENT_PRIVACY_SIGNAL_PREFERENCES,
  DEFAULT_STORAGE_KEY,
} from "~/components/cookie-consent/CookieConsentTypes"

const initialPreferences = JSON.stringify(COOKIE_CONSENT_INITIAL_PREFERENCES)
const privacySignalPreferences = JSON.stringify(
  COOKIE_CONSENT_PRIVACY_SIGNAL_PREFERENCES
)

interface Props {
  record?: CookieConsentRecord
  isConsentRequired: boolean
  shouldMountGTMFromGoogleDomain: boolean
  shouldMountGTMFromOurDomain: boolean
  gaProjectId: string
  originUrlForGTMServerSide: string
}

const CookieConsentWatcher: React.FC<Props> = ({
  record,
  isConsentRequired = false,
  shouldMountGTMFromGoogleDomain,
  shouldMountGTMFromOurDomain,
  gaProjectId,
  originUrlForGTMServerSide,
}) => {
  const adConsent = record?.preferences.marketing ? "granted" : "denied"
  const analyticsConsent = record?.preferences.analytics ? "granted" : "denied"
  // rendered before the GTM loader so gtm.js already sees the visitor's choice
  const preferences = JSON.stringify(record?.preferences)

  // the inline script already pushed the saved choice, only push new ones
  const restoredUpdatedAt = useRef(record?.updatedAt)
  const isChangedOnThisPage =
    record !== undefined && record.updatedAt !== restoredUpdatedAt.current

  return (
    <Helmet>
      {isConsentRequired && (
        <script>
          {`
          window.dataLayer = window.dataLayer || [];
          function gtag() {
            window.dataLayer.push(arguments);
          }
          // set the default and restore the saved choice before GTM loads,
          // React renders after it
          (function () {
            function toConsentState(consent) {
              var ad = consent.marketing ? 'granted' : 'denied';
              return {
                'ad_storage': ad,
                'ad_user_data': ad,
                'ad_personalization': ad,
                'analytics_storage': consent.analytics ? 'granted' : 'denied',
              };
            }

            // the browser privacy signal starts with everything optional off
            var hasPrivacySignal = navigator.globalPrivacyControl === true;
            var consent = hasPrivacySignal ? ${privacySignalPreferences} : ${initialPreferences};
            gtag('consent', 'default', toConsentState(consent));

            try {
              var saved = JSON.parse(localStorage.getItem('${DEFAULT_STORAGE_KEY}'));
              if (saved && saved.version === ${CONSENT_VERSION}) {
                // the signal wins over choices made before it was turned on
                var isOptedOut = hasPrivacySignal && saved.globalPrivacyControl !== true;
                consent.marketing = saved.preferences.marketing === true && !isOptedOut;
                consent.analytics = saved.preferences.analytics === true && !isOptedOut;
                gtag('consent', 'update', toConsentState(consent));
              }
            } catch (e) {}

            // sent on every page, with the defaults until the visitor chooses
            window.dataLayer.push({ event: 'cookie_consent_update', cookie_consent: consent });
          })();
        `}
        </script>
      )}
      {Boolean(isConsentRequired && isChangedOnThisPage) && (
        <script>
          {`
          gtag('consent', 'update', {
            'ad_storage': '${adConsent}',
            'ad_user_data': '${adConsent}',
            'ad_personalization': '${adConsent}',
            'analytics_storage': '${analyticsConsent}'
          });
          window.dataLayer.push({ event: "cookie_consent_update", cookie_consent: ${preferences} });
      `}
        </script>
      )}
      {shouldMountGTMFromGoogleDomain && (
        <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
      )}
      {shouldMountGTMFromGoogleDomain && (
        <script>{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gaProjectId}');`}</script>
      )}
      {shouldMountGTMFromOurDomain && (
        <script>{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'${originUrlForGTMServerSide}/8febf188cb6d6e67e579d9ed1f22b830/?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gaProjectId}');`}</script>
      )}
    </Helmet>
  )
}

export default CookieConsentWatcher
